import { Suspense, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Card, CardTitle } from "@/core/ui";
import { useTenant } from "@/core/tenant/TenantProvider";
import { RulesText, extractRules } from "@/core/puzzles/PuzzlePreviewPage";
import { getPuzzleType } from "@/puzzle-types/registry";
import type { Feedback } from "@/puzzle-types/types";
import { useExample, useExampleCheck, type ExamplePayload } from "./api";
import { dedupeFeedback } from "./PlayPage";

/**
 * A type's built-in example (ADR 0069): the same Player as a real run, checked by the type's
 * own verdict on the server, but with no attempt, no clock and no ranking. It is how a player
 * learns a type without spending the one blind ranked run (ADR 0068).
 *
 * `?from=<slug>` is the puzzle the player came from, to send them back once they have it.
 */
export function ExamplePage() {
  const { type = "" } = useParams();
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const example = useExample(type, i18n.language);
  const from = params.get("from");

  if (example.isPending) return <p className="bt-page-message">{t("app.loading")}</p>;
  if (example.isError || !example.data)
    return <Alert tone="danger">{t("example.unavailable")}</Alert>;
  return (
    <ExamplePlay
      // A new language is a new example (a word game): start it from scratch.
      key={`${type}:${i18n.language}`}
      payload={example.data}
      from={from}
    />
  );
}

function ExamplePlay({ payload, from }: { payload: ExamplePayload; from: string | null }) {
  const { t, i18n } = useTranslation();
  const tenant = useTenant();
  const module = getPuzzleType(payload.puzzle_type);
  const check = useExampleCheck(payload.puzzle_type, i18n.language);
  const fresh = () => (module ? module.emptyProgress(payload.definition) : null);
  const [progress, setProgress] = useState<unknown>(fresh);
  // What a check serialises: a Player may edit and ask to submit in the same tick.
  const progressRef = useRef<unknown>(progress);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [solved, setSolved] = useState(false);
  const [wrong, setWrong] = useState(false);

  if (!module)
    return <Alert tone="danger">{t("play.unknown_type", { type: payload.puzzle_type })}</Alert>;
  const Player = module.Player;
  const graded = payload.capabilities.graded_feedback;
  const typeLabel = t(`puzzle_types.${payload.puzzle_type}`, { defaultValue: payload.label });

  const onProgressChange = (next: unknown) => {
    progressRef.current = next;
    setProgress(next);
    setFeedback([]);
    setWrong(false);
  };

  const doCheck = () => {
    if (solved || check.isPending) return;
    const submission = module.serializeSubmission(payload.definition, progressRef.current);
    check.mutate(submission, {
      onSuccess: (result) => {
        setFeedback(result.feedback);
        if (module.applyVerdict) {
          const next = module.applyVerdict(payload.definition, progressRef.current, {
            correct: result.correct,
            submission,
            feedback: result.feedback,
          });
          progressRef.current = next;
          setProgress(next);
        }
        setSolved(result.correct);
        // A graded guess is information, not a failure (ADR 0041).
        setWrong(!result.correct && !graded);
      },
    });
  };

  const restart = () => {
    const next = fresh();
    progressRef.current = next;
    setProgress(next);
    setFeedback([]);
    setSolved(false);
    setWrong(false);
  };

  const rules = extractRules(payload.definition, i18n.language);
  return (
    <section className="bt-play">
      <div className="bt-play__column">
        <Card className="bt-card--sheet">
          <div className="bt-play__header">
            <div>
              <h1 className="bt-play__title">{t("example.title", { type: typeLabel })}</h1>
              {payload.title ? <p className="bt-play__author">{payload.title}</p> : null}
            </div>
            <div className="bt-play__status">
              <Badge tone="info">{t("example.badge")}</Badge>
            </div>
          </div>
          <div className="bt-play__notice">
            <Alert tone="info">{t("example.notice")}</Alert>
          </div>
          <Suspense fallback={<p className="bt-page-message">{t("app.loading")}</p>}>
            <Player
              definition={payload.definition}
              progress={progress}
              onProgressChange={onProgressChange}
              readOnly={solved}
              liveFeedback={payload.capabilities.live_feedback && tenant.live_feedback_enabled}
              constraintDefinitions={payload.constraint_definitions}
              locale={i18n.language}
              onRequestSubmit={doCheck}
            />
          </Suspense>
          <div className="bt-play__actions">
            {solved ? null : (
              <Button size="lg" onClick={doCheck} loading={check.isPending}>
                {t("play.submit")}
              </Button>
            )}
            <Button variant="ghost" onClick={restart}>
              {t("example.restart")}
            </Button>
          </div>
          {check.isError ? <Alert tone="danger">{t("app.error_generic")}</Alert> : null}
          {solved ? (
            <div className="bt-example__done">
              <Alert tone="success">{t("example.solved")}</Alert>
              {from ? (
                <Link
                  to={`/puzzles/${encodeURIComponent(from)}`}
                  className="bt-button bt-button--primary bt-button--lg"
                >
                  {t("example.back_to_puzzle")}
                </Link>
              ) : null}
            </div>
          ) : null}
          {wrong ? (
            <div className="bt-example__verdict">
              <Alert>{t("example.not_yet")}</Alert>
              {feedback.length > 0 ? (
                <ul className="bt-play__feedback">
                  {dedupeFeedback(feedback)
                    .slice(0, 6)
                    .map(({ code, params }) => (
                      <li key={code}>{t(`feedback.${code}`, { defaultValue: code, ...params })}</li>
                    ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </Card>
      </div>
      <div className="bt-play__column">
        <Card className="bt-card--panel">
          <CardTitle>{t("play.rules")}</CardTitle>
          <RulesText text={rules} />
        </Card>
        {from && !solved ? (
          <Link to={`/puzzles/${encodeURIComponent(from)}`} className="bt-example__back">
            {t("example.back_to_puzzle")}
          </Link>
        ) : null}
      </div>
    </section>
  );
}
